export default function initAudioBlock() {
    const $podcastContainer = document.querySelectorAll<HTMLElement>('.audio-wrapper .audio-player-container');

    $podcastContainer.forEach($container => {
        const $player = $container.querySelector<HTMLAudioElement>('.audio-player');

        if ($player) {
            new AudioBlock($container, $player);
        }
    });
}

export class AudioBlock {
    $container: HTMLElement;

    /** Le player HTML5 */
    $player: HTMLAudioElement;

    /** La barre de progression */
    $seekSlider: HTMLInputElement;

    /** Le bouton play/pause */
    $changeStateButton: HTMLElement;

    /** L'élément contenant le timer */
    $currentTimeContainer: HTMLElement;

    /** L'élément contenant la durée total de l'audio */
    $durationContainer: HTMLElement;

    constructor($container: HTMLElement, $player: HTMLAudioElement) {
        this.$container = $container;
        this.$player = $player;

        this.$changeStateButton = this.$container.querySelector('.play-button') as HTMLElement;
        this.$seekSlider = this.$container.querySelector('.seek-slider') as HTMLInputElement;
        this.$currentTimeContainer = this.$container.querySelector('.audio-player-progression .time.current-time') as HTMLElement;
        this.$durationContainer = this.$container.querySelector('.audio-player-progression .time.duration') as HTMLElement;

        if (this.$player.readyState > 0) { // Si le média est déjà chargé
            this.init();
        } else { // Sinon, on attend qu'il le soit
            this.$player.addEventListener('loadedmetadata', this.init.bind(this));
        }
    }

    /**
     * Initialisation du player lorsque le média est chargé
     * 
     * @return {void}
     */
    init(): void {
        // Mise à jour de la durée totale du média
        this.$durationContainer.innerText = this.convertTimeForHumans(this.$player.duration);

        // Lorsque l'on clique sur le bouton lecture/pause
        this.$changeStateButton.addEventListener('click', () => {
            if (this.$player.paused) {
                this.$player.play();
            } else {
                this.$player.pause();
            }
        });

        // Lorsque l'on clique sur la barre de progression
        this.$seekSlider.addEventListener('input', () => {
            this.$player.currentTime = (Number(this.$seekSlider.value) * this.$player.duration) / 100;
            this.updateProgressBar();
        });

        // Lorsque le média passe en lecture
        this.$player.addEventListener('play', () => {
            this.$changeStateButton.classList.remove('play-button--on-pause');
            this.$changeStateButton.classList.add('play-button--on-play');
        });

        // Lorsque le média passe en pause
        this.$player.addEventListener('pause', () => {
            this.$changeStateButton.classList.remove('play-button--on-play');
            this.$changeStateButton.classList.add('play-button--on-pause');
        });

        // Lorsque le média est en train d'être joué
        this.$player.addEventListener('timeupdate', () => {
            // Mise à jour du temps parcouru
            this.$currentTimeContainer.innerText = this.convertTimeForHumans(this.$player.currentTime);

            this.updateProgressBar();
        });

        // Lorsque le média est en train d'être chargé
        this.$player.addEventListener('progress', () => {
            this.updateBuffedBar();
        });

        // Lorsque le média est prêt à être lu
        this.$player.addEventListener('canplay', () => {
            this.updateBuffedBar();
        });
    }

    /**
     * Mise à jour de la barre de chargement
     * 
     * @return {void}
     */
    updateBuffedBar() : void
    {
        const bufferLength = this.$player.buffered.length;

        if (bufferLength > 0) {
            this.$container.style.setProperty('--seek-buffer-width', String(this.$player.buffered.end(bufferLength - 1) * 100 / this.$player.duration) + '%');
        }
    }

    /**
     * Mise à jour de la barre de progression
     * 
     * @return {void}
     */
    updateProgressBar() : void
    {
        this.$container.style.setProperty('--seek-progress-width', String(this.$player.currentTime * 100 / this.$player.duration) + '%');
    }

    /**
     * Convertis des secondes pour les humains (00:00:00)
     * 
     * @param {number} time
     * 
     * @return {string}
     */
    convertTimeForHumans(time: number) : string {
        const minutes = String(Math.floor(time % 3600 / 60)).padStart(2, '0');
        const seconds = String(Math.floor(time % 3600 % 60)).padStart(2, '0');
        
        return [minutes, seconds].join(':');
    }
}
